import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Info, Check, X, Bookmark, Clock,
  Code, Upload, Monitor, FileCode2, ExternalLink, Loader2,
} from 'lucide-react';
import { PageShell, PageHeader } from '../components/PeecUI';
import { peecAPI } from '../api/peecAPI';
import { useI18n } from '../i18n/useI18n';
import { RecommendationStatus } from '../db/recommendationSeeds';

interface OwnedPageProps {
  workspaceId: string;
}

// Types

type ContentType = 'Homepage' | 'How-To Guide' | 'Article' | 'Listicle' | 'Comparison';
type NavFilter = ContentType | 'history' | null;
type ActionStatus = RecommendationStatus;
type BuildPhase = 'idle' | 'building' | 'ready';

interface Recommendation {
  id: string;
  priority: 'Low' | 'Medium' | 'High';
  contentType: ContentType;
  description: string;
  status: ActionStatus;
}

// Static constants

const CONTENT_TYPE_DOT: Record<ContentType, string> = {
  'Homepage':     'bg-orange-400',
  'How-To Guide': 'bg-sky-500',
  'Article':      'bg-emerald-500',
  'Listicle':     'bg-emerald-500',
  'Comparison':   'bg-violet-400',
};

const PAGE_TYPES: ContentType[] = [
  'Homepage', 'How-To Guide', 'Article', 'Listicle', 'Comparison',
];

const CONTENT_TYPE_I18N_KEY: Record<ContentType, string> = {
  Homepage: 'owned.contentTypeHomepage',
  'How-To Guide': 'owned.contentTypeHowToGuide',
  Article: 'owned.contentTypeArticle',
  Listicle: 'owned.contentTypeListicle',
  Comparison: 'owned.contentTypeComparison',
};

const SUPPORTED_FORMATS = [
  '.zip -- project archive',
  '.tar.gz -- tarball',
  'Next.js / React / Vite project',
  'package.json + src/ folder',
];

const BUILD_STEPS = [
  '$ Extracting uploaded files...',
  '$ npm install -- resolving packages...',
  '$ npm run build -- compiling TypeScript...',
  '$ Bundling & optimizing assets...',
  '$ Starting preview server...',
  'Build complete (12.4 s) -- Preview ready',
];

// RecommendationCard

interface CardProps {
  rec: Recommendation;
  onStatusChange: (id: string, status: ActionStatus) => void;
}

const RecommendationCard: React.FC<CardProps> = ({ rec, onStatusChange }) => {
  const { t } = useI18n();
  const priorityKeyMap: Record<Recommendation['priority'], string> = {
    Low: 'owned.priorityLow',
    Medium: 'owned.priorityMedium',
    High: 'owned.priorityHigh',
  };
  const isDone = rec.status === 'done';
  return (
    <article
      className={`flex h-full min-h-0 flex-col rounded-xl border bg-white shadow-sm ${
        isDone ? 'border-emerald-100 opacity-60' : 'border-zinc-200'
      }`}
    >
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-300" aria-hidden="true" />
          <span className="text-xs text-zinc-500">{t(priorityKeyMap[rec.priority])}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${CONTENT_TYPE_DOT[rec.contentType]}`}
            aria-hidden="true"
          />
          <span className="text-xs font-medium text-zinc-600">{t(CONTENT_TYPE_I18N_KEY[rec.contentType])}</span>
        </div>
      </div>

      <div className="min-h-0 flex-1 px-3 pb-4">
        <p className="h-full overflow-y-auto pr-1 text-xs leading-relaxed text-zinc-700">
          {rec.description}
        </p>
      </div>

      <div className="flex items-center overflow-hidden rounded-b-xl border-t border-zinc-100">
        <button
          onClick={() => onStatusChange(rec.id, 'done')}
          aria-pressed={rec.status === 'done'}
          className={`flex flex-1 items-center justify-center gap-1 py-2.5 text-[11px] font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-300 ${
            rec.status === 'done'
              ? 'bg-emerald-50 text-emerald-600'
              : 'text-zinc-400 hover:text-emerald-600'
          }`}
        >
          <Check size={11} aria-hidden="true" />
          {t('owned.done')}
        </button>
        <div className="h-4 w-px shrink-0 bg-zinc-100" aria-hidden="true" />
        <button
          onClick={() => onStatusChange(rec.id, 'declined')}
          aria-pressed={rec.status === 'declined'}
          className={`flex flex-1 items-center justify-center gap-1 py-2.5 text-[11px] font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-300 ${
            rec.status === 'declined'
              ? 'bg-rose-50 text-rose-500'
              : 'text-zinc-400 hover:text-rose-500'
          }`}
        >
          <X size={11} aria-hidden="true" />
          {t('owned.decline')}
        </button>
        <div className="h-4 w-px shrink-0 bg-zinc-100" aria-hidden="true" />
        <button
          onClick={() => onStatusChange(rec.id, 'todo')}
          aria-pressed={rec.status === 'todo'}
          className={`flex flex-1 items-center justify-center gap-1 py-2.5 text-[11px] font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-300 ${
            rec.status === 'todo'
              ? 'bg-sky-50 text-sky-600'
              : 'text-zinc-400 hover:text-sky-600'
          }`}
        >
          <Bookmark size={11} aria-hidden="true" />
          {t('owned.todo')}
        </button>
      </div>
    </article>
  );
};

// ModifySourceCodeDialog

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  recommendations: Recommendation[];
}

const ModifySourceCodeDialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  recommendations,
}) => {
  const { t } = useI18n();
  const [phase, setPhase] = useState<BuildPhase>('idle');
  const [buildStep, setBuildStep] = useState('');
  const [buildDone, setBuildDone] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (isOpen) {
      setPhase('idle');
      setBuildStep('');
      setBuildDone(false);
      setCheckedIds(new Set());
      setUploadedFile(null);
    }
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [isOpen]);

  const startBuild = useCallback((file: File) => {
    setUploadedFile(file);
    setPhase('building');
    setBuildDone(false);
    timersRef.current.forEach(clearTimeout);
    timersRef.current = BUILD_STEPS.map((step, i) =>
      setTimeout(() => {
        setBuildStep(step);
        if (i === BUILD_STEPS.length - 1) {
          setBuildDone(true);
          setPhase('ready');
        }
      }, (i + 1) * 900)
    );
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) startBuild(file);
    },
    [startBuild]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) startBuild(file);
    },
    [startBuild]
  );

  const toggleCheck = useCallback((id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="修改源码"
    >
      <div className="mx-4 flex h-[640px] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-md">

        {/* Left sidebar */}
        <aside className="flex w-72 shrink-0 flex-col gap-3 border-r border-zinc-100 bg-white p-3">

          {/* Header */}
          <div className="rounded-xl border border-zinc-200 px-4 py-4">
            <div className="flex items-center gap-2">
              <div className="grid h-7 w-7 place-items-center rounded-lg bg-zinc-900">
                <Code size={14} className="text-white" aria-hidden="true" />
              </div>
              <span className="text-sm font-semibold text-zinc-900">{t('owned.modifySourceCode')}</span>
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-zinc-400">
              上传项目源码，在沙盒中构建并预览，然后一键应用所选优化建议。
            </p>
          </div>

          {/* Upload section */}
          <div className="rounded-xl border border-zinc-200 px-4 py-4">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
              上传源码
            </p>

            {!uploadedFile ? (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDrop}
                  className={`w-full rounded-xl border-2 border-dashed px-4 py-5 text-center outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
                    isDragOver
                      ? 'border-zinc-400 bg-zinc-100'
                      : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
                  }`}
                  aria-label="上传项目源码"
                >
                  <Upload size={18} className="mx-auto mb-2 text-zinc-400" aria-hidden="true" />
                  <p className="text-xs font-medium text-zinc-600">拖拽或点击上传</p>
                  <p className="mt-0.5 text-[11px] text-zinc-400">前端项目 / 服务端 API</p>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  name="source-code"
                  accept=".zip,.tar,.tar.gz,.tgz"
                  className="sr-only"
                  aria-label="选择项目归档文件"
                  onChange={handleFileChange}
                />
                <ul className="mt-3 space-y-1">
                  <li className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                    支持格式
                  </li>
                  {SUPPORTED_FORMATS.map((fmt) => (
                    <li key={fmt} className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                      <span className="h-1 w-1 shrink-0 rounded-full bg-zinc-300" aria-hidden="true" />
                      {fmt}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
                <FileCode2 size={14} className="shrink-0 text-zinc-500" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-zinc-800">{uploadedFile.name}</p>
                  <p className="text-[11px] text-zinc-400">
                    {(uploadedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Recommendations checklist */}
          <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-zinc-200">
            <p className="px-4 pt-3 pb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
              选择要应用的建议
            </p>
            <ul className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
              {recommendations.map((rec) => (
                <li key={rec.id}>
                  <label className="flex cursor-pointer items-start gap-2.5 rounded-lg p-2 hover:bg-zinc-50">
                    <input
                      type="checkbox"
                      checked={checkedIds.has(rec.id)}
                      onChange={() => toggleCheck(rec.id)}
                      className="mt-0.5 shrink-0 accent-zinc-900"
                      aria-label={`Apply: ${t(CONTENT_TYPE_I18N_KEY[rec.contentType])} -- ${rec.description.slice(0, 45)}`}
                    />
                    <div className="min-w-0">
                      <div className="mb-0.5 flex items-center gap-1.5">
                        <span
                          className={`h-1.5 w-1.5 shrink-0 rounded-full ${CONTENT_TYPE_DOT[rec.contentType]}`}
                          aria-hidden="true"
                        />
                        <span className="text-[11px] font-medium text-zinc-500">
                          {t(CONTENT_TYPE_I18N_KEY[rec.contentType])}
                        </span>
                      </div>
                      <p className="line-clamp-2 text-[11px] leading-relaxed text-zinc-600">
                        {rec.description}
                      </p>
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          {/* Footer buttons */}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="h-8 flex-1 rounded-lg border border-zinc-200 text-xs font-medium text-zinc-600 outline-none hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
            >
              {t('common.cancel')}
            </button>
            {phase === 'ready' && checkedIds.size > 0 && (
              <button
                onClick={onClose}
                className="h-8 flex-1 rounded-lg bg-zinc-900 text-xs font-medium text-white outline-none hover:bg-zinc-800 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
              >
                应用 ({checkedIds.size})
              </button>
            )}
          </div>
        </aside>

        {/* Right preview area */}
        <main className="flex min-w-0 flex-1 flex-col">

          {/* Single-line terminal status bar */}
          {(phase === 'building' || phase === 'ready') && (
            <div
              className={`flex h-9 shrink-0 items-center gap-2 border-b px-4 font-mono text-[11px] ${
                buildDone
                  ? 'border-emerald-900 bg-emerald-950 text-emerald-400'
                  : 'border-zinc-800 bg-zinc-900 text-emerald-400'
              }`}
            >
              {!buildDone ? (
                <Loader2 size={11} className="shrink-0 animate-spin" aria-hidden="true" />
              ) : (
                <Check size={11} className="shrink-0" aria-hidden="true" />
              )}
              <span className="truncate">{buildStep}</span>
            </div>
          )}

          {/* Preview content */}
          <div className="flex flex-1 items-center justify-center p-8">

            {phase === 'idle' && (
              <div className="text-center">
                <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-zinc-100">
                  <Monitor size={24} className="text-zinc-400" aria-hidden="true" />
                </div>
                <p className="text-sm font-medium text-zinc-600">等待上传项目</p>
                <p className="mt-1 text-xs text-zinc-400">
                  上传源码后将自动构建并在沙盒中预览
                </p>
              </div>
            )}

            {phase === 'building' && (
              <div className="text-center">
                <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-zinc-100">
                  <Loader2 size={24} className="animate-spin text-zinc-400" aria-hidden="true" />
                </div>
                <p className="text-sm font-medium text-zinc-600">沙盒构建中...</p>
                <p className="mt-1 text-xs text-zinc-400">正在编译项目，请稍候</p>
              </div>
            )}

            {phase === 'ready' && (
              <div className="w-full max-w-2xl">
                <div className="overflow-hidden rounded-xl border border-zinc-200 shadow-md">
                  {/* Browser chrome bar */}
                  <div className="flex items-center gap-2 border-b border-zinc-100 bg-zinc-50 px-3 py-2">
                    <div className="flex gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-rose-400" aria-hidden="true" />
                      <span className="h-2.5 w-2.5 rounded-full bg-amber-400" aria-hidden="true" />
                      <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" aria-hidden="true" />
                    </div>
                    <div className="flex flex-1 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1">
                      <span className="text-[11px] text-zinc-500">localhost:3000</span>
                      <ExternalLink size={10} className="ml-auto text-zinc-300" aria-hidden="true" />
                    </div>
                  </div>

                  {/* Simulated page skeleton */}
                  <div className="bg-white p-5">
                    <div className="mb-4 flex items-center justify-between border-b border-zinc-100 pb-3">
                      <div className="h-4 w-24 rounded bg-zinc-200" />
                      <div className="flex gap-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-3 w-14 rounded bg-zinc-100" />
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="mb-2 h-7 w-2/3 rounded bg-zinc-200" />
                      <div className="mb-1 h-3 w-full rounded bg-zinc-100" />
                      <div className="mb-1 h-3 w-5/6 rounded bg-zinc-100" />
                      <div className="mt-3 flex gap-2">
                        <div className="h-8 w-24 rounded-lg bg-zinc-900" />
                        <div className="h-8 w-20 rounded-lg border border-zinc-200 bg-white" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="rounded-lg border border-zinc-100 p-3">
                          <div className="mb-1.5 h-3 w-3/4 rounded bg-zinc-200" />
                          <div className="h-2 w-full rounded bg-zinc-100" />
                          <div className="mt-1 h-2 w-2/3 rounded bg-zinc-100" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-center text-[11px] text-zinc-400">
                  Sandbox preview -- select recommendations and click Apply to make changes
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

// OwnedPage

const OwnedPage: React.FC<OwnedPageProps> = ({ workspaceId }) => {
  const { t } = useI18n();
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [activeFilter, setActiveFilter] = useState<NavFilter>(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    peecAPI.getOwnedRecommendations(workspaceId)
      .then((recommendations) => {
        if (recommendations) {
          setRecs(recommendations as Recommendation[]);
        }
      })
      .catch((error) => console.error('Failed to fetch owned recommendations:', error));
  }, [workspaceId]);

  const updateStatus = useCallback((id: string, status: ActionStatus) => {
    setRecs((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    peecAPI.updateOwnedRecommendationStatus(workspaceId, id, status)
      .catch((error) => console.error('Failed to update owned recommendation status:', error));
  }, [workspaceId]);

  const activeRecs = recs.filter((r) => r.status === 'pending' || r.status === 'todo');
  const historyRecs = recs.filter((r) => r.status === 'done' || r.status === 'declined');
  const filtered =
    activeFilter === 'history'
      ? historyRecs
      : activeFilter === null
      ? activeRecs
      : activeRecs.filter((r) => r.contentType === activeFilter);

  const stats = {
    total: recs.length,
    done: recs.filter((r) => r.status === 'done').length,
    declined: recs.filter((r) => r.status === 'declined').length,
    todo: recs.filter((r) => r.status === 'todo').length,
  };

  return (
    <PageShell>
      <PageHeader title={t('owned.title')} description={t('owned.description')} />

      <div className="flex">
        {/* Left sidebar nav */}
        <nav className="w-48 shrink-0 border-r border-zinc-200 pr-6" aria-label="内容类型筛选">
          <button
            onClick={() => setActiveFilter(null)}
            aria-current={activeFilter === null ? 'page' : undefined}
            className={`mb-0.5 flex w-full items-center rounded-lg px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
              activeFilter === null
                ? 'border-l-2 border-cyan-500 bg-zinc-50 font-medium text-zinc-900 rounded-l-none'
                : 'text-zinc-500 hover:bg-zinc-100/70 hover:text-zinc-800 border-l-2 border-transparent rounded-l-none'
            }`}
          >
            {t('owned.overview')}
          </button>

          <div className="mt-3">
            <div className="mb-1 flex items-center gap-1 px-2.5">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                {t('owned.pageTypes')}
              </span>
              <Info size={10} className="text-zinc-300" aria-hidden="true" />
            </div>
            <ul className="space-y-0.5">
              {PAGE_TYPES.map((type) => (
                <li key={type}>
                  <button
                    onClick={() => setActiveFilter(type)}
                    aria-current={activeFilter === type ? 'page' : undefined}
                    className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
                      activeFilter === type
                        ? 'border-l-2 border-cyan-500 bg-zinc-50 font-medium text-zinc-900 rounded-l-none'
                        : 'text-zinc-500 hover:bg-zinc-100/70 hover:text-zinc-800 border-l-2 border-transparent rounded-l-none'
                    }`}
                  >
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-300"
                      aria-hidden="true"
                    />
                    {t(CONTENT_TYPE_I18N_KEY[type])}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-3">
            <div className="mb-1 flex items-center gap-1 px-2.5">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                {t('owned.history')}
              </span>
            </div>
            <button
              onClick={() => setActiveFilter('history')}
              aria-current={activeFilter === 'history' ? 'page' : undefined}
              className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
                activeFilter === 'history'
                  ? 'border-l-2 border-cyan-500 bg-zinc-50 font-medium text-zinc-900 rounded-l-none'
                  : 'text-zinc-500 hover:bg-zinc-100/70 hover:text-zinc-800 border-l-2 border-transparent rounded-l-none'
              }`}
            >
              <Clock size={13} className="shrink-0" aria-hidden="true" />
              {t('owned.history')}
            </button>
          </div>
        </nav>

        {/* Main content */}
        <div className="min-w-0 flex-1 pl-6 divide-y divide-zinc-200">

          {/* Heading section */}
          <div className="pb-5">
            <p className="mb-1 text-xs text-zinc-400">
              {activeFilter === 'history'
                ? t('owned.history')
                : activeFilter
                ? t(CONTENT_TYPE_I18N_KEY[activeFilter])
                : t('owned.overview')}
            </p>
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-xl font-semibold tracking-tight text-zinc-950">
                {activeFilter === 'history'
                  ? t('owned.historyTitle')
                  : activeFilter
                  ? `${t(CONTENT_TYPE_I18N_KEY[activeFilter])} ${t('owned.recommendationsSuffix')}`
                  : t('owned.allRecommendations')}
              </h2>
              <button
                onClick={() => setShowDialog(true)}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 text-xs font-medium text-white outline-none hover:bg-zinc-800 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
              >
                <Code size={13} aria-hidden="true" />
                {t('owned.modifySourceCode')}
              </button>
            </div>
          </div>

          {/* Stats section */}
          <div className="py-5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { label: t('owned.allOwnedActions'), value: stats.total },
                { label: t('owned.doneActions'), value: stats.done },
                { label: t('owned.skippedActions'), value: stats.declined },
                { label: t('owned.todoActions'), value: stats.todo },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm"
                >
                  <p className="text-xs text-zinc-500">{label}</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums text-zinc-950">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations section */}
          <div className="pt-5">
            <h3 className="text-sm font-semibold text-zinc-950">
              {activeFilter === 'history' ? t('owned.historyTitle') : t('owned.allRecommendations')}
            </h3>
            <p className="mt-0.5 text-xs text-zinc-400">
              {activeFilter === 'history' ? t('owned.historySubtitle') : t('owned.allRecommendationsSubtitle')}
            </p>
            <div className="mt-4 overflow-x-auto pb-2">
              <div className="flex min-w-max gap-3">
                {filtered.map((rec) => (
                  <div key={rec.id} className="h-[180px] w-[260px] shrink-0 sm:w-[300px]">
                    <RecommendationCard rec={rec} onStatusChange={updateStatus} />
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>

      <ModifySourceCodeDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        recommendations={recs}
      />
    </PageShell>
  );
};

export default OwnedPage;
